import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { findMiniAppScreen } from '@tfa/shared'
import { useCurator } from '../contexts/CuratorContext'

// Curator-client deeplink: `cc-<uuid>` from tracking-summary keyboard in
// curator's forum group. Sends curator straight to that client's profile in
// Mini App so per-meal/per-training comments resolve tracking, instead of
// the old mass-resolve buttons (🍽️/🏋️).
const CURATOR_CLIENT_PREFIX = 'cc-'
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * On first mount, if the Mini App was launched with a `startapp` parameter,
 * navigate accordingly. Runs once — subsequent in-app navigation is never
 * overridden, so back/forward work normally.
 *
 * Recognised forms:
 *   - `<screen-key>` — one of MINI_APP_SCREENS (home, diary, ...)
 *   - `cc-<uuid>` — curator-client profile (`/curator/clients/<uuid>`)
 *
 * Reads `start_param` directly from `window.Telegram.WebApp.initDataUnsafe` so the
 * hook works regardless of SDKProvider presence (safe in MockApp / dev-outside-TG).
 */
export function useStartParamRouting() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isCurator, switchToCuratorMode, exitClient } = useCurator()
  // Запоминаем последнее значение start_param, которое уже обработали —
  // чтобы не зацикливать навигацию на тех же данных, но РЕАГИРОВАТЬ если
  // start_param реально изменился (Telegram пересоздал вкладку Mini App
  // с новым deeplink).
  const lastHandledParam = useRef<string | null>(null)

  useEffect(() => {
    const tg = (window as unknown as {
      Telegram?: {
        WebApp?: {
          initDataUnsafe?: { start_param?: string }
          onEvent?: (event: string, cb: () => void) => void
          offEvent?: (event: string, cb: () => void) => void
        }
      }
    }).Telegram

    const tryRoute = () => {
      const raw = tg?.WebApp?.initDataUnsafe?.start_param
      if (typeof raw !== 'string' || !raw) return
      // Уже обработали именно это значение — не дублируем navigate (если
      // куратор сам перешёл в другое окно Mini App, мы не хотим его
      // насильно возвращать).
      if (lastHandledParam.current === raw) return

      if (raw.startsWith(CURATOR_CLIENT_PREFIX)) {
        const clientId = raw.slice(CURATOR_CLIENT_PREFIX.length)
        if (!UUID_RE.test(clientId)) return
        const targetPath = `/curator/clients/${clientId}`
        // Если уже на этом профиле — отмечаем как обработанное и выходим.
        if (location.pathname === targetPath) {
          lastHandledParam.current = raw
          return
        }
        lastHandledParam.current = raw
        ;(async () => {
          // Mini App мог стартовать в client-режиме (auth.service.ts:139
          // возвращает client-тип, если пользователь сам-себе-куратор и
          // недавно логинился как клиент). В client-Routes роут
          // /curator/clients/:id не матчится → fallback `<Navigate to="/">`,
          // куратор попадает на Home вместо профиля. Перелогиниваемся
          // как curator + чистим viewingClientId, чтобы AuthenticatedApp
          // выбрал curator-Routes (см. App.tsx:122).
          if (!isCurator) {
            try { await switchToCuratorMode() } catch { /* ignore */ }
          } else {
            // Куратор был внутри клиента (impersonation) — выходим, иначе
            // viewingClientId блокирует curator-Routes.
            exitClient()
          }
          // Явно открываем «Дневник» — куратор приходит сюда из tracking-сводки,
          // где висят meal-/workout-отчёты для коммента, не программа.
          navigate(targetPath, { replace: true, state: { tab: 'diary' } })
        })()
        return
      }

      const screen = findMiniAppScreen(raw)
      if (!screen) return
      if (location.pathname === screen.path) {
        lastHandledParam.current = raw
        return
      }
      lastHandledParam.current = raw
      navigate(screen.path, { replace: true })
    }

    // Первый раз — сразу при mount.
    tryRoute()

    // Telegram WebApp API не имеет события «start_param изменился» (start_param
    // фиксируется на launch и не обновляется без re-init). Но при повторном
    // клике deeplink Telegram K **иногда** создаёт новую вкладку Mini App →
    // новый iframe → mount, а **иногда** только pings событиями SDK без re-init.
    // Подписки и polling страхуют второй случай: если start_param реально
    // обновился без mount — перечитываем и навигируем.
    //
    // Polling 750мс — дешёво (string compare), не нагружает; останавливается
    // на unmount автоматически.
    const webApp = tg?.WebApp
    const pollId = setInterval(tryRoute, 750)

    if (webApp?.onEvent && webApp?.offEvent) {
      webApp.onEvent('viewportChanged', tryRoute)
      webApp.onEvent('themeChanged', tryRoute)
      return () => {
        clearInterval(pollId)
        webApp.offEvent?.('viewportChanged', tryRoute)
        webApp.offEvent?.('themeChanged', tryRoute)
      }
    }
    return () => {
      clearInterval(pollId)
    }
  }, [navigate, location.pathname, isCurator, switchToCuratorMode, exitClient])
}
